import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { useParams, useLocation } from "react-router-dom";
import EnhancedTable from "../components/common/EnhancedTable";
import AgentViewModal from "../components/dialogs/AgentViewModal";
import StatusChip from "../utils/StatusChip";
import { fetchGroupAgents } from "../store/slices/groupagentsSlice";

const GroupAgentsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { agents, loading, pagination } = useAppSelector((state: any) => state.groupAgents);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Get groupId from route params
  const { groupId: routeGroupId } = useParams();
  const groupId = routeGroupId || '';

  const location = useLocation();
  const passedGroup = location.state?.group;

  // Use the same Agent interface and columns as UsersList
  interface Agent {
    id: string;
    firstname?: string | null;
    lastname?: string | null;
    username?: string | null;
    email?: string | null;
    cellnumber?: string | null;
    agentcode?: string | null;
    roleId?: number;
    groupId?: string;
    userstatus?: string | number | null;
    created?: string;
    modified?: string;
    group?: {
      id?: string;
      name?: string;
    };
  }

  const columns = [
    {
      id: 'firstname',
      label: 'First Name',
      minWidth: 140,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.firstname || '-'}</Typography>
      ),
    },
    {
      id: 'lastname',
      label: 'Last Name',
      minWidth: 140,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.lastname || '-'}</Typography>
      ),
    },
    {
      id: 'cellnumber',
      label: 'Cell Number',
      minWidth: 150,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.cellnumber || '-'}</Typography>
      ),
    },
    {
      id: 'agentcode',
      label: 'Agent Code',
      minWidth: 140,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.agentcode || '-'}</Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 180,
      sortable: true,
      draggable: true,
      format: (value: string, row: Agent) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{row.email || '-'}</Typography>
      ),
    },
    {
      id: 'userstatus',
      label: 'Status',
      minWidth: 120,
      align: 'center' as const,
      sortable: true,
      draggable: true,
      format: (value: number, row: Agent) => (
        <StatusChip status={row.userstatus ?? 0} />
      ),
    },
  ];

  useEffect(() => {
    const params: any = {
      groupId,
      page: currentPage,
      items_per_page: itemsPerPage,
    };
    if (sortField) params.sort = `${sortField} ${sortDirection}`;
    dispatch(fetchGroupAgents(params));
  }, [dispatch, groupId, currentPage, itemsPerPage, sortField, sortDirection]);

  const handleViewAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setViewModalOpen(true);
  };

  // Use passedGroup for header info, fallback to agents[0]?.group if not present
  const groupName = passedGroup?.name || agents[0]?.group?.name || '-';
  const groupStatus =
    passedGroup?.status === 1 || passedGroup?.status === 'ACTIVE' || passedGroup?.status === true
      ? 'Active'
      : (agents[0]?.group?.status === 1 || agents[0]?.group?.status === 'ACTIVE' || agents[0]?.group?.status === true)
        ? 'Active'
        : 'Inactive';
  const totalAgents = pagination?.total || agents.length;

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          background: '#fff',
          borderRadius: 2,
          boxShadow: 1,
          p: 3,
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Group Name
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary">
            {groupName}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Status
          </Typography>
          <Typography variant="h6" fontWeight={500} color={groupStatus === 'Active' ? 'success.main' : 'error.main'}>
            {groupStatus}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Total Agents
          </Typography>
          <Typography variant="h5" fontWeight={700} color="secondary">
            {totalAgents}
          </Typography>
        </Box>
      </Box>

      {/* Table Section */}
      <Box>
        <EnhancedTable
          columns={columns}
          data={agents}
          loading={loading}
          showActions={true}
          emptyMessage="No agents are associated with this group."
          showSelection={false}
          enableColumnReordering
          useExternalPagination
          pagination={pagination}
          onView={handleViewAgent}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
          }}
          useExternalSorting
          onSort={(field) => {
            if (sortField === field) {
              setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
            } else {
              setSortField(field);
              setSortDirection('asc');
            }
            setCurrentPage(1);
          }}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          entityName="agent"
        />
      </Box>
      {/* Agent View Modal */}
      <AgentViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
      />
    </Box>
  );
};

export default GroupAgentsList;